import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import { Box, CircularProgress, IconButton, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { LuX } from 'react-icons/lu'
import { Document, Page, pdfjs } from 'react-pdf'

import Icon from '@/components/Icon'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

interface PDFViewerModalProps {
  open: boolean
  pdfUrl: string
  pdfName: string
  onClose: () => void
  containerRef?: React.RefObject<HTMLDivElement>
}

const PDFViewerModal = ({
  open,
  pdfUrl,
  pdfName,
  onClose,
  containerRef,
}: PDFViewerModalProps) => {
  const [numPages, setNumPages] = useState<number | null>(null)
  const [loadingPdf, setLoadingPdf] = useState(true)
  const [zoomLevel, setZoomLevel] = useState(1.0)

  useEffect(() => {
    if (!open) {
      setZoomLevel(1.0)
      setNumPages(null)
      setLoadingPdf(true)
    }
  }, [open])

  if (!open) return null
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1300,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: 'rgba(0, 0, 0, 0.6)',
      }}
      onClick={onClose}
    >
      <Box
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: '60%',
          height: '80%',
          bgcolor: 'white',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1,
            bgcolor: '#F5F5F5',
            borderBottom: '1px solid #ccc',
          }}
        >
          <Typography fontWeight={500} fontSize="14px" noWrap color="black">
            {`${pdfName}.pdf`}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() =>
                setZoomLevel((prev) =>
                  Math.max(0.5, Number.parseFloat((prev - 0.1).toFixed(1))),
                )
              }
              sx={{ color: 'black', fontSize: '16px' }}
            >
              <ZoomOutIcon />
            </IconButton>
            <Typography fontSize="14px" color="black">
              {Math.round(zoomLevel * 100)}%
            </Typography>
            <IconButton
              onClick={() =>
                setZoomLevel((prev) =>
                  Math.min(3, Number.parseFloat((prev + 0.1).toFixed(1))),
                )
              }
              sx={{ color: 'black', fontSize: '16px' }}
            >
              <ZoomInIcon />
            </IconButton>

            <Icon
              icon={LuX}
              size={18}
              onClick={onClose}
              color="black"
              style={{ marginLeft: 8, cursor: 'pointer' }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            px: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: loadingPdf ? 'center' : 'flex-start',
          }}
        >
          <Document
            file={pdfUrl}
            onLoadSuccess={({ numPages }) => {
              setNumPages(numPages)
              setLoadingPdf(false)
            }}
            loading={
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CircularProgress style={{ color: '#ffb572' }} />
              </Box>
            }
          >
            {Array.from(new Array(numPages || 0), (_, index) => (
              <Box key={`page_${index + 1}`}>
                <Page
                  pageNumber={index + 1}
                  width={containerRef?.current?.offsetWidth || 600}
                  scale={zoomLevel}
                />
                {index < (numPages || 0) - 1 && (
                  <Box height="20px" bgcolor={'#f5f5f5'} />
                )}
              </Box>
            ))}
          </Document>
        </Box>
      </Box>
    </Box>
  )
}

export default PDFViewerModal
